import React, { useEffect, useState, useRef } from 'react'
import { store } from 'reduxStore'
import { Dialog } from '@progress/kendo-react-dialogs'
import { Field, Form, Formik } from 'formik'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { Grid, Box } from '@mui/material'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import ModalTitle from 'components/Common/ModalTitle'
import TextField from 'components/Common/TextField'
import { AccountFormField } from 'components/Common/AccountFormLayout'
import ZendeskLink from 'components/Common/ZendeskLink'
import MaskField from 'components/Common/MaskField'
import Button from 'components/Common/Button'
import CCIcon from 'assets/payment/credit-card-grey.svg'
import * as yup from 'yup'
import './index.css'
import api from 'api'
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from 'themes/theme'
const stripePromise = loadStripe(process.env['REACT_APP_STRIPE_PUBLIC_KEY'])

const sx = {
  root: {
    color: 'var(--primary)',
    fontFamily: 'Poppins',
    maxWidth: 900,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '40px',
    '& button': {
      padding: '0 30px',
    },
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: -0.37,
  },
  text: {
    fontSize: '14px',
    marginTop: '20px',
  },
  textNoData: {
    fontSize: '14px',
  },
  paymentListWrapper: {
    marginTop: '40px',
  },
  bold: {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  card: {
    boxSizing: 'border-box',
    borderLeft: '10px solid #D3D3DF',
    borderRadius: '10px',
    background: '#FFF',
    boxShadow: '4px 4px 15px 0px #0A78CD14',
  },
  cardBody: {
    padding: '20px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    gap: '20px',
  },
  ccIcon: {
    marginRight: 5,
  },
  cardText: {
    fontSize: 14,
    textAlign: 'left',
    flex: 1,
  },
  txtBtn: {
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '14px',
  },
  btnstyle: {
    width: '230px',
    height: '50px'
  }
}

const addPaymentSchema = yup.object({
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
  address: yup.string().required('Required'),
  city: yup.string().required('Required'),
  state: yup.string().required('Required'),
  zipcode: yup.string().required('Required'),
  cardNumber: yup
    .string()
    .required('Required')
    .matches(/^[0-9-]+$/, 'Invalid Card Number'),
  mmyy: yup
    .string()
    .required('Required')
    .test('month', 'InvalidDate', value => {
      try {
        let isValid = true
        const inputMonth = value.slice(0, 2)
        const inputYear = value.slice(value.length - 2)
        const currentMonth = moment().format('MM')
        const currentYear = moment().format('YY')
        if (+inputYear < +currentYear) isValid = false
        if (!['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].includes(inputMonth))
          isValid = false
        if (inputYear === currentYear && +inputMonth < +currentMonth) isValid = false
        return isValid
      } catch (e) {
        return false
      }
    })
    .matches(/^[0-9/]+$/, 'Required'),
  cvc: yup
    .string()
    .required('Required')
    .matches(/^[0-9]+$/, 'Required'),
})

const addPaymentInitialValues = {
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  cardNumber: '',
  mmyy: '',
  cvc: '',
}

const Payment = props => {
  const formik = useRef(null)
  const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false)
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(null)
  const [isPaymentConfirmDialogOpen, setIsPaymentConfirmDialogOpen] = useState(false)
  const [isPaymentFailDialogOpen, setIsPaymentFailDialogOpen] = useState(false)
  const user = store.getState().userReducer.user
  const { accountType } = user
  const [loading, setLoading] = useState(true)
  const [paymentList, setPaymentList] = useState([])

  const dashboardInfo = useSelector(state => state.apiResponseReducers.dashboardInfo)
  const getStripePaymentList = async () => {
    try {
      if (!user) return
      setLoading(true)
      const paymentRes = await api.getStripePaymentList()
      setPaymentList(paymentRes?.details || [])
      setLoading(false)
    } catch (error) {
      console.error('error-getStripePaymentList-', error)
      setLoading(false)
    }
  }

  const makeDefaultStripePayment = async payment => {
    try {
      setLoading(true)
      await api.makeDefaultStripePayment({ cardInfoId: payment?.cardInfoId, defaultPayment: true })
      await getStripePaymentList()
      setLoading(false)
    } catch (error) {
      console.error('error-makeDefaultStripePayment-', error)
      setLoading(false)
    }
  }

  const deleteStripePayment = async payment => {
    try {
      setLoading(true)
      await api.deleteStripePayment({ cardInfoId: payment?.cardInfoId })
      await getStripePaymentList()
      setIsDeleteConfirmDialogOpen(false)
      setLoading(false)
    } catch (error) {
      console.error('error-deleteStripePayment-', error)
      setLoading(false)
    }
  }

  const onFormSubmit = async (values, formikHelpers) => {
    formikHelpers.setSubmitting(true)
    try {
      const expiryMonth = values.mmyy.slice(0, 2)
      const expiryYear = `${moment().format('YYYY').slice(0, 2)}${values.mmyy.slice(values.mmyy.length - 2)}`
      const reqBody = {
        ...values,
        cardNumber: values.cardNumber.replaceAll('-', ''),
        expiryMonth,
        expiryYear,
      }

      const addPaymentRes = await api.addNewStripePayment(reqBody)
      if (addPaymentRes?.response_status === 200) {
        setIsAddPaymentDialogOpen(false)
        setIsPaymentConfirmDialogOpen(true)
        await getStripePaymentList()
        return
      }
      // Enable this if you want to set BE error message
      // setIsPaymentFailDialogOpen(addPaymentRes?.data?.errors || true)
      setIsPaymentFailDialogOpen(true)
      setIsAddPaymentDialogOpen(false)
    } catch (error) {
      console.error(error)
      setIsPaymentFailDialogOpen(true)
    } finally {
      setIsAddPaymentDialogOpen(false)
      formikHelpers.setSubmitting(false)
    }
  }

  const renderPaymentCard = payment => {
    const isDeleteDisabled = paymentList.length === 1
    return (
      <Box sx={sx.cardBody} key={payment?.cardInfoId}>
        <Box sx={sx.cardText}>
          <Box component={"img"} sx={sx.ccIcon} src={CCIcon} alt="cc" /> {payment?.cardType || 'Visa'}
        </Box>
        <Box sx={sx.cardText} style={{ flex: 1.5 }}>
          &bull;&bull;&bull;&bull; {payment?.displayCard}
        </Box>
        <Box sx={sx.cardText} style={{ flex: 2 }}>
          Exp. {`${payment?.expiryMonth || ''}/${payment?.expiryYear || ''}` || ''}
        </Box>
        <Box sx={sx.cardText} style={{ flex: 3 }}>
          Added on {payment?.addedOn || ''}
        </Box>
        <Box
          sx={{ ...sx.cardText, ...sx.txtBtn }}
          style={{ flex: 2, color: '#0A78CD' }}
          onClick={() => makeDefaultStripePayment(payment)}
        >
          {payment.defaultPayment ? 'Default' : 'Make Default'}
        </Box>
        <Box
          sx={{ ...sx.cardText, ...sx.txtBtn }}
          style={isDeleteDisabled ? { color: 'grey', cursor: 'not-allowed' } : { color: '#D14343' }}
          onClick={() => {
            if (isDeleteDisabled) return
            setIsDeleteConfirmDialogOpen(payment)
          }}
        >
          Delete
        </Box>
      </Box >
    )
  }

  useEffect(() => {
    getStripePaymentList()
    // eslint-disable-next-line
  }, [user])

  const defaultPaymentMethod = paymentList?.filter(p => !!p.defaultPayment)?.[0] || {}
  const inActivePaymentMethods = paymentList?.filter(p => !p.defaultPayment)

  // console.log('NewPayments-states-', { defaultPayment, inActivePaymentMethods, paymentList })

  const FreePayment = () => {
    const navigate = useNavigate()

    const onClickStartNow = e => {
      e && e.preventDefault()
      navigate('/welcome')
    }

    return (
      <>
        <Box sx={sx.root}>
          <Box sx={sx.bold} marginBottom="30px">
            Manage your current payment method
          </Box>
          <Button onClick={onClickStartNow} style={{ marginBottom: '300px' }}>
            Add Payment Method
          </Button>
        </Box>
        <ZendeskLink />
      </>
    )
  }

  if (dashboardInfo.user_info.badged === true) {
    return (
      <Box paddingBottom="3" sx={sx.root}>
        <Box sx={sx.label}>Your current payment method</Box>

        <Box sx={{ display: 'flex', fontSize: '14px' }} marginTop={3}>
          Your monthly subscription is being sponsored by&nbsp;
          <span>{dashboardInfo.user_info.epName === 'EPSG' && <p>EPSG</p>}</span>
          <span>{dashboardInfo.user_info.epName === 'MLS' && <p>Merchant Lynx Services</p>}</span>
        </Box>
      </Box>
    )
  }
  if (loading) {
    return (
      <Box sx={sx.root}>
        <Box display='flex' justifyContent='center' alignItems='center' height='50vh'>
          <ThreeDotsLoadingSpinner width="60px" />
        </Box>
      </Box>
    )
  }

  if (accountType === 'free') {
    return <FreePayment />
  }

  return (
    <Box sx={sx.root}>
      {isAddPaymentDialogOpen && (
        <Dialog
          className="NewPaymentAddPaymentDialog"
          onClose={() => setIsAddPaymentDialogOpen(false)}
          height={'70%'}
          width={'40%'}
        >
          <div className="NewPaymentAddPaymentDialogWrapper">
            <ModalTitle
              title="Add Payment Method"
              version={2}
              onClose={() => setIsAddPaymentDialogOpen(false)}
              closeIcon={'false'}
            ></ModalTitle>
            <div className="NewPaymentAddPaymentBody">
              <Elements stripe={stripePromise}>
                <Formik
                  innerRef={formik}
                  validationSchema={addPaymentSchema}
                  initialValues={addPaymentInitialValues}
                  onSubmit={onFormSubmit}
                  enableReinitialize
                >
                  {formikProps => {
                    const { isSubmitting } = formikProps
                    return (
                      <Form style={{ flex: 1 }} autoComplete="off">
                        <Grid container spacing={2} style={{ marginTop: 5 }} className="customAuthInput">
                          <Grid item xs={6}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="firstName">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="First Name"
                                    fullWidth
                                    rows="8"
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={6}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="lastName">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="Last Name"
                                    fullWidth
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={12}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="address">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="Address"
                                    fullWidth
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={4}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="city">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="City"
                                    fullWidth
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={4}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="state">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="State"
                                    fullWidth
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={4}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="zipcode">
                                {({ field, meta }) => (
                                  <TextField
                                    required
                                    bgwhite
                                    {...field}
                                    label="Zip"
                                    fullWidth
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    // disabled={true}
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={8}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="cardNumber">
                                {({ field, meta }) => (
                                  <MaskField
                                    icon={CCIcon}
                                    isMasked
                                    maskPatten="0000-0000-0000-0000"
                                    required
                                    {...field}
                                    fullWidth
                                    label="Card Number"
                                    bgwhite
                                    zeroMarginLabel
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    isFormatHint={false}
                                    autoComplete="new-password"
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={2}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="mmyy">
                                {({ field, meta }) => (
                                  <MaskField
                                    isMasked
                                    maskPatten="00/00"
                                    required
                                    bgwhite
                                    {...field}
                                    fullWidth
                                    label="MM/YY"
                                    zeroMarginLabel
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    isFormatHint={false}
                                    autoComplete="new-password"
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                          <Grid item xs={2}>
                            <AccountFormField style={{ height: 75, marginBottom: '10px' }}>
                              <Field name="cvc">
                                {({ field, meta }) => (
                                  <MaskField
                                    maskPatten="000"
                                    isMasked
                                    required
                                    bgwhite
                                    {...field}
                                    fullWidth
                                    label="CVC"
                                    zeroMarginLabel
                                    error={Boolean(meta.touched && meta.error)}
                                    helperText={meta.touched && meta.error}
                                    isFormatHint={false}
                                    autoComplete="new-password"
                                  />
                                )}
                              </Field>
                            </AccountFormField>
                          </Grid>
                        </Grid>
                        <div className="AddPaymentFormButtons">
                          <Button type={SECONDARY_BUTTON} sx={sx.btnstyle} onClick={() => setIsAddPaymentDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button submit disabled={isSubmitting} sx={sx.btnstyle} type={PRIMARY_BUTTON} loading={isSubmitting}>
                            Submit
                          </Button>
                        </div>
                      </Form>
                    )
                  }}
                </Formik>
              </Elements>
            </div>
            <div className="DisclosureFormFooter">
              <ZendeskLink />
            </div>
          </div>
        </Dialog>
      )}
      {isPaymentConfirmDialogOpen && (
        <Dialog className="PaymentConfirmDialog" onClose={() => setIsPaymentConfirmDialogOpen(false)}>
          <div className="PaymentConfirmDialogWrapper">
            <ModalTitle
              version={2}
              title={`Success!`}
              content={'You will receive confirmation via email shortly​.'}
              onClose={() => setIsPaymentConfirmDialogOpen(false)}
              icon={'success'}
              closeIcon={'false'}
            />
            <div className="PaymentConfirmFooter">
              <Button type="submit" sx={sx.btnstyle} onClick={() => setIsPaymentConfirmDialogOpen(false)}>
                Close
              </Button>
            </div>
            <div style={{ marginTop: 30 }}>
              <ZendeskLink />
            </div>
          </div>
        </Dialog>
      )}
      {isPaymentFailDialogOpen && (
        <Dialog className="PaymentFailDialog" onClose={() => setIsPaymentFailDialogOpen(false)}>
          <div className="PaymentFailDialogWrapper">
            <ModalTitle
              version={2}
              title="Unable to Add Payment Method"
              // Disabled since we don't have STD error message comming from BE team
              // in future if require enable below code to display error message as per BE/API error
              // title={
              //   typeof isPaymentFailDialogOpen === 'string' ? isPaymentFailDialogOpen : `Unable to Add Payment Method​`
              // }
              onClose={() => setIsPaymentFailDialogOpen(false)}
              icon={'error'}
              closeIcon={'false'}
            />
            <div className="PaymentFailBody"></div>
            <div className="PaymentFailFooter">
              <Button type={SECONDARY_BUTTON} sx={sx.btnstyle} onClick={() => setIsPaymentFailDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" sx={sx.btnstyle} onClick={() => {setIsPaymentFailDialogOpen(false); setIsAddPaymentDialogOpen(true)}}>
                Try Again
              </Button>
            </div>
            <div style={{ marginTop: 30 }}>
              <ZendeskLink />
            </div>
          </div>
        </Dialog>
      )}
      {isDeleteConfirmDialogOpen && (
        <Dialog className="DeleteConfirmDialog" onClose={() => setIsDeleteConfirmDialogOpen(false)}>
          <div className="DeleteConfirmDialogWrapper">
            <ModalTitle
              version={2}
              title={'Confirmation'}
              content={`Please confirm you want to delete payment method ending in ${isDeleteConfirmDialogOpen?.displayCard}`}
              onClose={() => setIsDeleteConfirmDialogOpen(false)}
              closeIcon={'false'}
            />
            <div className="DeleteConfirmBody"></div>
            <div className="DeleteConfirmFooter">
              <Button type={SECONDARY_BUTTON} sx={sx.btnstyle} onClick={() => setIsDeleteConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button negative sx={sx.btnstyle} onClick={() => deleteStripePayment(isDeleteConfirmDialogOpen)}>
                Delete
              </Button>
            </div>
          </div>
        </Dialog>
      )}
      <Box margin="2%">
        <Box sx={sx.header}>
          <Box sx={sx.label}>Your payment methods</Box>
          <div>
            <Button onClick={() => setIsAddPaymentDialogOpen(true)}>Add Payment Method</Button>
          </div>
        </Box>
        <Box sx={sx.paymentListWrapper}>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <ThreeDotsLoadingSpinner width="60px" />
            </Box>
          ) : paymentList.length === 0 ? (
            <Box sx={sx.textNoData}>No payment method</Box>
          ) : (
            <>
              {Object.keys(defaultPaymentMethod).length > 0 ? (
                <Box sx={sx.card} style={{ marginBottom: 20, borderLeft: '10px solid #469CF8' }}>
                  {renderPaymentCard(defaultPaymentMethod)}
                </Box>
              ) : null}
              <Box sx={sx.card}>
                {inActivePaymentMethods.map(payment => {
                  return renderPaymentCard(payment)
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>
      {/* <Box color="#212529">
          <ZendeskLink />
        </Box> */}
    </Box>
  )
}

export default Payment
